/**
 * Per-layer colour palette for the JF schematic preview and the soil
 * summary panel.
 *
 * Colours are keyed by the layer's **trimmed, lower-cased soil
 * description** so two segments with the same name (e.g. a "Clay"
 * band that reappears below a "Sand" lens) render in the same hue
 * and the operator can read them as one logical layer at a glance.
 *
 * Slots are assigned in first-occurrence order rather than via a
 * string hash — hashing 12 slots collides on as few as ~3 names by
 * the birthday paradox, and the operator was correctly catching the
 * collisions in the wild. `buildSoilColourMap` walks the row list
 * once and hands out PALETTE[0], PALETTE[1], … to each new name as
 * it appears, wrapping after 12 distinct names. Callers should
 * build the map once at the data root and pass it down.
 */
const PALETTE = [
  "#8b5a2b", // brown
  "#ca8a04", // ochre
  "#65a30d", // moss
  "#475569", // slate
  "#0ea5e9", // sky
  "#a16207", // mid-brown
  "#a855f7", // purple
  "#ef4444", // red
  "#10b981", // emerald
  "#f97316", // orange
  "#14b8a6", // teal
  "#db2777", // pink
];

const UNNAMED = "#9ca3af"; // neutral grey for rows with no description yet

function nameKey(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Walk the soil rows and assign each distinct name a palette slot in
 * first-occurrence order. Returns a Map keyed by the normalised
 * (trimmed, lower-cased) name. Unnamed rows are skipped.
 *
 * The map is the authoritative source of truth for layer colours in
 * one render. Pass it to `soilColour(name, map)` everywhere a colour
 * is needed.
 */
export function buildSoilColourMap(
  rows: ReadonlyArray<{ soilType: string }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const r of rows) {
    const key = nameKey(r.soilType);
    if (key.length === 0) continue;
    if (!map.has(key)) {
      map.set(key, PALETTE[map.size % PALETTE.length]!);
    }
  }
  return map;
}

/**
 * Look up a colour for a soil description.
 *
 * Preferred usage: pass the map produced by `buildSoilColourMap` so
 * the colour matches the rest of the page deterministically and
 * collision-free.
 *
 * Without a map, falls back to first-occurrence assignment against
 * an internal scratch map built from a single name — fine for the
 * "I have no surrounding context" callers but offers no collision
 * guarantee across multiple invocations.
 */
export function soilColour(
  key: number | string,
  map?: Map<string, string>,
): string {
  if (typeof key === "number") {
    if (!Number.isFinite(key) || key < 0) return PALETTE[0]!;
    return PALETTE[Math.floor(key) % PALETTE.length]!;
  }
  const normalised = nameKey(key);
  if (normalised.length === 0) return UNNAMED;
  if (map) {
    const hit = map.get(normalised);
    if (hit) return hit;
  }
  // Standalone fallback: assign slot 0 deterministically. Callers
  // that need distinct colours across multiple names should always
  // supply a map.
  return PALETTE[0]!;
}
